import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, X, Monitor, Gamepad2, Cpu, Headphones, Smartphone, HardDrive, Settings, Wrench, ShoppingCart, Star, Zap } from 'lucide-react';
import axios from 'axios';
import './AdCarousel.css';

const AdPopupCarousel = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [slides, setSlides] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const baseURL = window.APP_CONFIG?.API_URL || "https://backendweb.properteck.lk";


  // Map icon names to Lucide React components
  const iconMap = {
    Gamepad2: <Gamepad2 size={48} />,
    Monitor: <Monitor size={48} />,
    Cpu: <Cpu size={48} />,
    Headphones: <Headphones size={48} />,
    Smartphone: <Smartphone size={48} />,
    HardDrive: <HardDrive size={48} />,
    Settings: <Settings size={48} />,
    Wrench: <Wrench size={48} />
  };

  const autoPlayDuration = 4000; // 4 seconds

  // Fetch ads from backend
  useEffect(() => {
    const fetchAds = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(`${baseURL}/api/ads?page=1&limit=100`);
        if (response.data.success) {
          const currentDate = new Date();
          const activeAds = response.data.data
            .filter(ad => ad.isActive && 
              (!ad.startDate || new Date(ad.startDate) <= currentDate) &&
              (!ad.endDate || new Date(ad.endDate) >= currentDate))
            .sort((a, b) => b.priority - a.priority) // Sort by priority (descending)
            .map(ad => ({
              id: ad._id,
              title: ad.title,
              description: ad.description,
              subtitle: ad.subtitle,
              cta: ad.cta,
              icon: iconMap[ad.icon] || <Gamepad2 size={48} />, // Fallback to Gamepad2 if icon not found
              gradient: ad.gradient,
              discount: ad.discount,
              category: ad.category,
              targetUrl: ad.targetUrl,
              image: ad.image ? `${baseURL}/uploads/ads/${ad.image}` : null
            }));
          setSlides(activeAds);
        }
      } catch (error) {
        console.error('Error fetching ads:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAds();
  }, []);

  // Show popup automatically when component mounts and slides are loaded
  useEffect(() => {
    if (isLoading || slides.length === 0) return;

    const timer = setTimeout(() => {
      setIsPopupVisible(true);
    }, 1500); // Show after 1.5 seconds
    return () => clearTimeout(timer);
  }, [isLoading, slides.length]);

  // Auto-play functionality
  useEffect(() => {
    if (!isPopupVisible || isPaused || slides.length === 0) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, autoPlayDuration);

    return () => clearInterval(interval);
  }, [isPopupVisible, isPaused, slides.length]);

  // Progress bar animation
  useEffect(() => {
    if (!isPopupVisible || isPaused || slides.length === 0) {
      setProgress(0);
      return;
    }

    setProgress(0);
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          return 0;
        }
        return prev + (100 / (autoPlayDuration / 100));
      });
    }, 100);

    return () => clearInterval(progressInterval);
  }, [currentSlide, isPopupVisible, isPaused, slides.length]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isPopupVisible) return;

      switch (e.key) {
        case 'ArrowLeft':
          handlePrevSlide();
          break;
        case 'ArrowRight':
          handleNextSlide();
          break;
        case 'Escape':
          closePopup();
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isPopupVisible]);

  const handleNextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  }, [slides.length]);

  const handlePrevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  }, [slides.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const closePopup = () => {
    setIsPopupVisible(false);
    setProgress(0);
  };

  const handleCTAClick = (slide) => {
    console.log(`Clicked CTA for ${slide.title}`);
    if (slide.targetUrl) {
      window.location.href = slide.targetUrl;
    } else {
      alert(`Redirecting to ${slide.title} page...`);
    }
    closePopup();
  };

  const handleMouseEnter = () => {
    setIsPaused(true);
  };

  const handleMouseLeave = () => {
    setIsPaused(false);
  };

  // Don't render if popup shouldn't be visible or still loading
  if (!isPopupVisible || isLoading || slides.length === 0) return null;

  return (
    <div className="ad-popup-carousel-overlay">
      <div
        className="ad-popup-carousel-content"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Close Button */}
        <button onClick={closePopup} className="ad-popup-carousel-close-btn">
          <X size={24} color="white" />
        </button>

        {/* Carousel Container */}
        <div className="ad-popup-carousel-container">
          {slides.map((slide, index) => (
            <div
              key={slide.id}
              className={`ad-popup-carousel-slide ${slide.gradient} ${index === currentSlide ? 'ad-popup-carousel-active' : ''}`}
            >
              <div className="ad-popup-carousel-slide-content">
                {/* Discount Badge */}
                <div className="ad-popup-carousel-discount-badge">
                  <Zap size={16} />
                  {slide.discount}
                </div>

                {/* Content */}
                <div className="ad-popup-carousel-slide-icon">
                  {slide.icon}
                </div>
                <h2 className="ad-popup-carousel-slide-title">
                  {slide.title}
                </h2>
                <p className="ad-popup-carousel-slide-description">
                  {slide.description}
                </p>
                <p className="ad-popup-carousel-slide-subtitle">
                  {slide.subtitle}
                </p>
                <button
                  onClick={() => handleCTAClick(slide)}
                  className="ad-popup-carousel-slide-cta-btn"
                >
                  <ShoppingCart size={20} />
                  {slide.cta}
                </button>
              </div>
            </div>
          ))}

          {/* Navigation Arrows */}
          <button onClick={handlePrevSlide} className="ad-popup-carousel-nav-arrow ad-popup-carousel-prev">
            <ChevronLeft size={24} color="white" />
          </button>
          <button onClick={handleNextSlide} className="ad-popup-carousel-nav-arrow ad-popup-carousel-next">
            <ChevronRight size={24} color="white" />
          </button>

          {/* Progress Bar */}
          <div className="ad-popup-carousel-progress-bar-container">
            <div className="ad-popup-carousel-progress-bar" style={{ width: `${progress}%` }} />
          </div>
        </div>

        {/* Navigation Dots */}
        <div className="ad-popup-carousel-navigation-dots">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`ad-popup-carousel-dot ${index === currentSlide ? 'ad-popup-carousel-active' : ''}`}
            />
          ))}
        </div>

        {/* Bottom Info Bar */}
        <div className="ad-popup-carousel-bottom-info-bar">
          <div className="ad-popup-carousel-info-content">
            <div className="ad-popup-carousel-info-item">
              <Star size={16} color="#eab308" />
              <span>4.9/5 Customer Rating</span>
            </div>
            <span className="ad-popup-carousel-info-separator">•</span>
            <span className="ad-popup-carousel-info-text">Free Shipping on Orders</span>
            <span className="ad-popup-carousel-info-separator">•</span>
            <span className="ad-popup-carousel-info-text">2-Year Warranty</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdPopupCarousel;